// src/App.tsx
import './App.css';

import { useMemo, useState } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

import { ColorModeContext } from './theme/ColorModeContext';
import { checkIfSignIn } from '@utils/user';

import Root from '@pages/Root';
import Error from '@pages/Error';
import Login from '@pages/Login';
import ForgotPassword from '@pages/ForgotPassword';

import AuthRoute from './AuthRoute';
import AdminRoute from './AdminRoute';
import PartnerRoute from './PartnerRoute';

import OutletWithContext from './routes/OutletWithContext';

// Shared (Admin + Partner)
import Orders from '@pages/orders/Orders';
import OrderDetail from '@pages/orders/OrderDetail';
import ChangePassword from '@pages/account/ChangePassword';

// Admin
import Home from '@pages/Home';

import Partners from '@pages/partners/Partners';
import AddPartner from '@pages/partners/AddPartner';
import PartnerDetail from '@pages/partners/PartnerDetail';
import PartnerPriceList from '@pages/partners/PartnerPriceList';
import PartnerPriceUpload from '@pages/partners/PartnerPriceUpload';
import { PartnerPriceUploadDetail } from '@pages/partners/PartnerPriceUploadDetail';

import Users from '@pages/users/Users';
import UserCreate from '@pages/users/UserCreate';
import UserEdit from '@pages/users/UserEdit';

import Products from '@pages/product/Products';
import AddProduct from '@pages/product/AddProduct';
import ProductDetail from '@pages/product/ProductDetail';
import ProductBulkUpload from '@pages/product/ProductBulkUpload';
import ProductBulkUploadDetail from '@pages/product/ProductBulkUploadDetail';

import Codes from '@pages/stock/Codes';
import AddStockCode from '@pages/stock/AddStockCode';
import EditStockCode from '@pages/stock/EditStockCode';
import EsimInventories from '@pages/stock/EsimInventories';
import EsimInventoryDetail from '@pages/stock/EsimInventoryDetail';
import Suppliers from '@pages/stock/Suppliers';
import AddSupplier from '@pages/stock/AddSupplier';
import EditSupplier from '@pages/stock/EditSupplier';
import { EsimBulkUpload } from '@pages/stock/EsimBulkUpload';
import { EsimBulkUploadDetail } from '@pages/stock/EsimBulkUploadDetail';
import { EsimBulkUpdate } from '@pages/stock/EsimBulkUpdate';
import { EsimBulkUpdateDetail } from '@pages/stock/EsimBulkUpdateDetail';

// Internal tools (dev only)
import ApiRunner from '@pages/internal/apiRunner/ApiRunner';

// Partner
import MyDetail from '@pages/partners/MyDetail';
import MyPartnerPriceList from '@pages/partners/MyPartnerPriceList';
import { PartnerProducts } from '@pages/product/PartnerProducts';
import { PartnerProductDetail } from '@pages/product/PartnerProductDetail';
import { AddOrder } from '@pages/orders/AddOrder';

// Public exchange flow (no auth required)
import { ExchangeMain } from './exchange/main';
import { ExchangeError } from './exchange/error';
import { ExchangeSuccess } from './exchange/success';

const RecaptchaLogin = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}>
    <Login />
  </GoogleReCaptchaProvider>
);

/**
 * App router definition.
 *
 * Policies:
 * - Root loader(checkIfSignIn) hydrates auth state without login flicker.
 * - AuthRoute gates authenticated pages once.
 * - AdminRoute / PartnerRoute are scoped so that only relevant paths are evaluated.
 * - AppPageHeader/Breadcrumb UI is driven by route handles (useMatches in Root).
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <Error />,
    loader: checkIfSignIn,
    children: [
      {
        element: <AuthRoute />,
        children: [
          // Shared: Orders
          {
            path: 'orders',
            element: <OutletWithContext />,
            handle: { crumb: 'Orders' },
            children: [
              {
                index: true,
                element: <Orders />,
                handle: {
                  crumb: 'Order List',
                  pageHeader: {
                    title: 'Order List',
                    description: 'Search, filter and manage all orders across partners and products.',
                  },
                },
              },
              {
                element: <PartnerRoute />,
                children: [
                  {
                    path: 'add',
                    element: <AddOrder />,
                    handle: {
                      crumb: 'Create Order',
                      pageHeader: {
                        title: 'Create Order',
                        description: 'Create a manual order.',
                        back: true,
                      },
                    },
                  },
                ],
              },
              {
                path: ':orderId',
                element: <OrderDetail />,
                handle: {
                  crumb: 'Order Detail',
                  pageHeader: {
                    title: 'Order Detail',
                    description: 'Review order information and status changes.',
                    back: true,
                    leading: 'slot',
                    actions: 'slot',
                  },
                },
              },
            ],
          },

          // Shared: Account
          {
            path: 'account',
            element: <OutletWithContext />,
            handle: { crumb: 'Account' },
            children: [
              {
                path: 'change-password',
                element: <ChangePassword />,
                handle: {
                  crumb: 'Change Password',
                  pageHeader: {
                    title: 'Change Password',
                    description: 'Update your password to keep your account secure.',
                    back: true,
                    maxWidth: 900,
                  },
                },
              },
            ],
          },

          // Partner-only scope
          {
            element: <PartnerRoute />,
            children: [
              {
                path: 'my-detail',
                element: <OutletWithContext />,
                handle: { crumb: 'Partner Info' },
                children: [
                  {
                    index: true,
                    element: <MyDetail />,
                    handle: {
                      crumb: 'Partner Info',
                      pageHeader: {
                        title: 'Partner Info',
                        description: 'Partner information is currently read-only for this role.',
                        back: true,
                      },
                    },
                  },
                  {
                    path: 'prices',
                    element: <MyPartnerPriceList />,
                    handle: {
                      crumb: 'My Price List',
                      pageHeader: {
                        title: 'My Price List',
                        description: 'Product authorization and pricing (partner view).',
                        back: true,
                      },
                    },
                  },
                ],
              },

              {
                path: 'my-products',
                element: <OutletWithContext />,
                handle: { crumb: 'Products' },
                children: [
                  {
                    index: true,
                    element: <PartnerProducts />,
                    handle: {
                      crumb: 'My Products',
                      pageHeader: {
                        title: 'My Products',
                        description: 'Search and manage products available for your account.',
                      },
                    },
                  },
                  {
                    path: ':productId',
                    element: <PartnerProductDetail />,
                    handle: {
                      crumb: 'Product Detail',
                      pageHeader: {
                        title: 'My Products',
                        description: 'Product details',
                        back: true,
                        leading: 'slot',
                        actions: 'slot',
                      },
                    },
                  },
                ],
              },
            ],
          },

          // Admin-only scope
          {
            element: <AdminRoute />,
            children: [
              {
                path: 'home',
                element: <Home />,
                handle: {
                  crumb: 'Dashboard',
                  pageHeader: {
                    title: 'Dashboard',
                    description: 'Overview of recent activity and key metrics.',
                  },
                },
              },

              {
                path: 'partners',
                element: <OutletWithContext />,
                handle: { crumb: 'Partners' },
                children: [
                  {
                    index: true,
                    element: <Partners />,
                    handle: {
                      crumb: 'Partner List',
                      pageHeader: {
                        title: 'Partner List',
                        description: 'Search and manage partner accounts.',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: 'add',
                    element: <AddPartner />,
                    handle: {
                      crumb: 'Add Partner',
                      pageHeader: {
                        title: 'Add Partner',
                        description: 'Create a new partner and generate credentials for portal and API access.',
                        back: true,
                        maxWidth: 900,
                      },
                    },
                  },
                  {
                    path: ':partnerId',
                    element: <PartnerDetail />,
                    handle: {
                      crumb: 'Partner Detail',
                      pageHeader: {
                        title: 'Partner Detail',
                        description: 'Manage partner account, API settings, pricing access, and balance.',
                        back: true,
                        leading: 'slot',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: ':partnerId/prices',
                    element: <PartnerPriceList />,
                    handle: {
                      crumb: 'Price List',
                      pageHeader: {
                        title: 'Price List',
                        description: 'Product authorization and partner-specific pricing.',
                        back: true,
                        leading: 'slot',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: ':partnerId/prices/upload',
                    element: <PartnerPriceUpload />,
                    handle: {
                      crumb: 'Upload Prices',
                      pageHeader: {
                        title: 'Upload Prices',
                        description: 'Bulk price upload',
                        back: true,
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: ':partnerId/prices/upload/:uploadId',
                    element: <PartnerPriceUploadDetail />,
                    handle: {
                      crumb: 'Price Upload Detail',
                      pageHeader: {
                        title: 'Price Upload Detail',
                        description: 'Bulk price upload detail',
                        back: true,
                      },
                    },
                  },
                ],
              },

              {
                path: 'users',
                element: <OutletWithContext />,
                handle: { crumb: 'Users' },
                children: [
                  {
                    index: true,
                    element: <Users />,
                    handle: {
                      crumb: 'User List',
                      pageHeader: {
                        title: 'User List',
                        description: 'Manage admin users (create, view, and edit).',
                        back: true,
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: 'new',
                    element: <UserCreate />,
                    handle: {
                      crumb: 'Create User',
                      pageHeader: {
                        title: 'Create User',
                        description: 'Create an admin account (Partner accounts are managed separately).',
                        back: true,
                        actions: 'slot',
                        maxWidth: 900,
                      },
                    },
                  },
                  {
                    path: ':userId',
                    element: <UserEdit />,
                    handle: {
                      crumb: 'User Edit',
                      pageHeader: {
                        title: 'User Edit',
                        description:
                          'ID and Name are read-only. You can update Group, Position, Email, Phone, and Status.',
                        back: true,
                        leading: 'slot',
                        actions: 'slot',
                        maxWidth: 900,
                      },
                    },
                  },
                ],
              },

              {
                path: 'products',
                element: <OutletWithContext />,
                handle: { crumb: 'Products' },
                children: [
                  {
                    index: true,
                    element: <Products />,
                    handle: {
                      crumb: 'Product List',
                      pageHeader: {
                        title: 'Product List',
                        description: 'Search, filter and manage products that partners can sell.',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: 'upload',
                    element: <OutletWithContext />,
                    handle: { crumb: 'Product Bulk Upload' },
                    children: [
                      {
                        index: true,
                        element: <ProductBulkUpload />,
                        handle: {
                          crumb: 'Product Bulk Upload',
                          pageHeader: {
                            title: 'Product Bulk Upload',
                            description:
                              'Upload products in bulk using Excel template and review validation results before final submission.',
                            actions: 'slot',
                          },
                        },
                      },
                      {
                        path: ':uploadId',
                        element: <ProductBulkUploadDetail />,
                        handle: {
                          crumb: 'Product Bulk Upload Detail',
                          pageHeader: {
                            title: 'Product Bulk Upload Detail',
                            description: 'Upload details',
                            back: true,
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'add',
                    element: <AddProduct />,
                    handle: {
                      crumb: 'Add Product',
                      pageHeader: {
                        title: 'Add Product',
                        description: 'Create a new product.',
                        back: true,
                        maxWidth: 900,
                      },
                    },
                  },
                  {
                    path: ':productId',
                    element: <ProductDetail />,
                    handle: {
                      crumb: 'Product Detail',
                      pageHeader: {
                        title: 'Product Detail',
                        description: 'Product detail - information',
                        back: true,
                        maxWidth: 900,
                        leading: 'slot',
                        actions: 'slot',
                      },
                    },
                  },
                ],
              },

              {
                path: 'stocks',
                element: <OutletWithContext />,
                handle: { crumb: 'Stocks' },
                children: [
                  {
                    path: 'codes',
                    element: <Codes />,
                    handle: {
                      crumb: 'Stock Codes (SKU)',
                      pageHeader: {
                        title: 'Stock Codes',
                        description: 'Search, monitor, and manage stock codes used for inventory operations.',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: 'codes/new',
                    element: <AddStockCode />,
                    handle: {
                      crumb: 'Create',
                      pageHeader: {
                        title: 'Create Stock Code (SKU)',
                        description: 'Create a new stock code used for inventory operations.',
                        back: true,
                        maxWidth: 900,
                      },
                    },
                  },
                  {
                    path: 'codes/:stockId/edit',
                    element: <EditStockCode />,
                    handle: {
                      crumb: 'Edit',
                      pageHeader: {
                        title: 'Edit Stock Code (SKU)',
                        description: 'Update configuration and status for the selected stock code.',
                        back: true,
                        maxWidth: 900,
                        leading: 'slot',
                      },
                    },
                  },

                  {
                    path: 'esim-inventory',
                    element: <OutletWithContext />,
                    handle: { crumb: 'SIM Inventory' },
                    children: [
                      {
                        index: true,
                        element: <EsimInventories />,
                        handle: {
                          crumb: 'SIM Inventory',
                          pageHeader: {
                            title: 'SIM Inventory',
                            description: 'Search and monitor all SIM and eSIM inventories managed in the system.',
                          },
                        },
                      },
                      {
                        path: ':esimInventoryId',
                        element: <EsimInventoryDetail />,
                        handle: {
                          crumb: 'SIM Inventory Detail',
                          pageHeader: {
                            title: 'SIM Inventory Detail',
                            description: 'View and manage detailed information for the selected SIM or eSIM inventory.',
                            back: true,
                            leading: 'slot',
                          },
                        },
                      },
                    ],
                  },

                  {
                    path: 'upload',
                    element: <OutletWithContext />,
                    handle: { crumb: 'Bulk Inventory Upload' },
                    children: [
                      {
                        index: true,
                        element: <EsimBulkUpload />,
                        handle: {
                          crumb: 'Bulk Inventory Upload',
                          pageHeader: {
                            title: 'Bulk Inventory Upload',
                            description: 'Create new eSIM inventory records by uploading an Excel file.',
                            leading: {
                              title: 'Creates new inventory records',
                              description: 'All rows in the uploaded file will be added as new inventory items.',
                            },
                            actions: 'slot',
                          },
                        },
                      },
                      {
                        path: ':uploadId',
                        element: <EsimBulkUploadDetail />,
                        handle: {
                          crumb: 'Bulk Inventory Upload Details',
                          pageHeader: {
                            title: 'Bulk Inventory Upload Details',
                            description: 'Review uploaded inventory records before confirming creation.',
                            back: true,
                            leading: 'slot',
                          },
                        },
                      },
                    ],
                  },

                  {
                    path: 'update',
                    element: <OutletWithContext />,
                    handle: { crumb: 'Bulk Inventory Update' },
                    children: [
                      {
                        index: true,
                        element: <EsimBulkUpdate />,
                        handle: {
                          crumb: 'Bulk Inventory Update',
                          pageHeader: {
                            title: 'Bulk Inventory Update',
                            description: 'Update status and expiration dates for existing eSIM inventories in bulk.',
                            leading: {
                              title: 'Updates existing inventory data',
                              description:
                                'This action modifies current records. Please review the template carefully before uploading.',
                            },
                            actions: 'slot',
                          },
                        },
                      },
                      {
                        path: ':updateId',
                        element: <EsimBulkUpdateDetail />,
                        handle: {
                          crumb: 'Bulk Inventory Update Details',
                          pageHeader: {
                            title: 'Bulk Inventory Update Details',
                            description: 'Review changes applied to existing inventory records before confirmation.',
                            back: true,
                            leading: 'slot',
                          },
                        },
                      },
                    ],
                  },

                  {
                    path: 'suppliers',
                    element: <Suppliers />,
                    handle: {
                      crumb: 'Suppliers',
                      pageHeader: {
                        title: 'Suppliers',
                        description:
                          'Manage supplier codes used across Stock and Product. Supplier settings determine the integration layer for API operations.',
                        actions: 'slot',
                      },
                    },
                  },
                  {
                    path: 'suppliers/new',
                    element: <AddSupplier />,
                    handle: {
                      crumb: 'Create',
                      pageHeader: {
                        title: 'Add Supplier',
                        description: 'Create a new supplier and contact information.',
                        back: true,
                        maxWidth: 900,
                      },
                    },
                  },
                  {
                    path: 'suppliers/:supplierId/edit',
                    element: <EditSupplier />,
                    handle: {
                      crumb: 'Edit',
                      pageHeader: {
                        title: 'Edit Supplier',
                        description: 'Update supplier details and contact information.',
                        back: true,
                        maxWidth: 900,
                        leading: 'slot',
                      },
                    },
                  },
                ],
              },

              // Internal tools — dev only. Excluded from production builds.
              ...(process.env.NODE_ENV === 'development'
                ? [
                    {
                      path: 'internal/api-runner',
                      element: <ApiRunner />,
                      handle: {
                        pageHeader: {
                          title: 'External API Runner',
                          description:
                            'Local testing for /external/partner/v1/* endpoints',
                        },
                      },
                    },
                  ]
                : []),
            ],
          },
        ],
      },
    ],
  },

  // Public routes
  { path: 'login', element: <RecaptchaLogin /> },
  { path: 'forgot-password', element: <ForgotPassword /> },

  // Public exchange flow (no auth required)
  {
    path: 'exchange',
    children: [
      { index: true, element: <ExchangeMain /> },
      { path: ':encryptedEsimId', element: <ExchangeSuccess /> },
      { path: 'error', element: <ExchangeError /> },
    ],
  },
]);

function App() {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    }),
    [mode],
  );

  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
        {isDev && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
